import { BACKOFFICE_WEBSITES, COOKIE_AUTH_SITES } from './constants'
// eslint-disable-next-line import/no-cycle
import { isAnySiteIncludesInRedirectUrl } from './login/loginUtils'
import { BACKOFFICE_GROUP_ID, YOUTUBE_BACKOFFICE_GROUP_ID } from './mamba/constants'
import { isBackofficeWebsite } from './utils'

const envVars = Object.fromEntries(
  Object.entries(process.env).map(([key, value]) => [key.replace('REACT_APP_', ''), value]),
)

// The window based overrides allow for runtime/out-of-deploy configuration, e.g. to allow FE PR envs to target different BE envs on the fly.
envVars.ENV = window.env !== undefined ? window.env : envVars.ENV
envVars.PR_ID = window.PR_ID !== undefined ? window.PR_ID : envVars.PR_ID
envVars.YOUTUBE_ENV =
  window.isYoutube !== undefined ? window.isYoutube : envVars.YOUTUBE_ENV === 'true'
envVars.USE_GATEWAY =
  window.useGateway !== undefined ? window.useGateway : envVars.USE_GATEWAY === 'true'
envVars.IS_CHINA_ENV =
  window.isChinaEnv !== undefined ? window.isChinaEnv : envVars.IS_CHINA_ENV === 'true'
envVars.IS_EUS_ENV = window.isEusEnv !== undefined ? window.isEusEnv : envVars.IS_EUS_ENV === 'true'
envVars.IS_NORTH_EUROPE_ENV =
  window.isNorthEuropeEnv !== undefined
    ? window.isNorthEuropeEnv
    : envVars.IS_NORTH_EUROPE_ENV === 'true'
envVars.IS_PARTNER_APP =
  window.isPartnerApp !== undefined ? window.isPartnerApp : envVars.IS_PARTNER_APP === 'true'

const { searchParams } = new URL(window.location.href)

let isInternalCookieAuthSite = false

// for login page's requests redirecting to internal apps which use cookies
if (envVars.WEBSITE === 'login') {
  const redirectPath = searchParams.get('redirect')
  if (redirectPath) {
    isInternalCookieAuthSite = isAnySiteIncludesInRedirectUrl(redirectPath, BACKOFFICE_WEBSITES)
  }
} else {
  // for requests of internal apps which use cookies
  isInternalCookieAuthSite = isBackofficeWebsite(envVars.WEBSITE)
}

const backofficeGroupId = envVars.YOUTUBE_ENV ? YOUTUBE_BACKOFFICE_GROUP_ID : BACKOFFICE_GROUP_ID

const userInfoKey =
  (envVars.ENV === 'dev' && 'WSC_DEV') ||
  (envVars.ENV === 'uat' && 'WSC_UAT') ||
  (envVars.ENV === 'pr' && 'WSC_PR' + envVars.PR_ID) ||
  'WSC'

const userInfoKeyPrefix = isBackofficeWebsite(envVars.WEBSITE) ? 'BACKOFFICE_' : ''
const uatToStagingMapping = 'login'
const servicesWithStagingEnv = ['mamba']

function getServiceUrlParts(serviceName, useGateWay) {
  let serviceSuffix =
    (envVars.ENV === 'dev' && `${useGateWay ? 'dev-eus2' : '-dev'}.clipro.tv`) ||
    (envVars.ENV === 'uat' && `${useGateWay ? 'uat-eus2' : '-uat'}.clipro.tv`) ||
    (envVars.ENV === 'prod' &&
      `${
        useGateWay
          ? envVars.IS_EUS_ENV
            ? 'prod-eus'
            : envVars.IS_NORTH_EUROPE_ENV
            ? 'eu'
            : 'prod-eus2'
          : ''
      }.clipro.tv`) ||
    (envVars.ENV === 'pr' &&
      `${useGateWay ? `pr${envVars.PR_ID}-eus2` : `-pr${envVars.PR_ID}`}.wscsports.dev`) ||
    (envVars.ENV === 'staging' &&
      servicesWithStagingEnv.includes(serviceName) &&
      `${useGateWay ? 'prod-eus2' : '-staging'}.clipro.tv`) ||
    (envVars.ENV === 'staging' && `${useGateWay ? 'prod-eus2' : ''}.clipro.tv`) ||
    '' // This is a case for the unit tests in order to make them work.

  const servicePrefix = envVars.YOUTUBE_ENV ? 'youtube-' : ''

  if (!useGateWay && uatToStagingMapping.includes(serviceName)) {
    serviceSuffix = serviceSuffix.replace('uat', 'staging')
  }

  if (envVars.IS_CHINA_ENV) {
    // TODO: rename clipro service back to clipro.clipro.tv after shmul is done with the CDN shenanigens
    // Chinese clipro is still clipro.clipro.cn and the subdomain names need to match
    if (serviceName === 'cliprocdn') {
      serviceName = 'clipro'
    }

    serviceSuffix = serviceSuffix.replace('.tv', '.cn')
  }

  if (envVars.ENV === 'staging' && useGateWay && servicesWithStagingEnv.includes(serviceName)) {
    serviceName = `${serviceName}staging`
  }

  return { serviceName, serviceSuffix, servicePrefix }
}

export const getServiceUrl = (serviceName) => {
  const useGateWay = envVars.USE_GATEWAY
  const {
    serviceName: actualServiceName,
    serviceSuffix,
    servicePrefix,
  } = getServiceUrlParts(serviceName, useGateWay)

  return useGateWay
    ? `https://${serviceSuffix}/api/${actualServiceName}` // No "servicePrefix" for useGatway as the new region doesn't have a YT env.
    : `https://${servicePrefix}${actualServiceName}${serviceSuffix}`
}

// returns BE service url to enable working with apps with limited internet access
export const getRestrictedUrl = (serviceName, restrictedGateway) => {
  const { serviceName: actualServiceName, serviceSuffix } = getServiceUrlParts(serviceName, false)
  // Delete when moving to new region - restricted gateway only in - old region + prod
  // TODO: mind the useGateway for the new region.
  return `https://${restrictedGateway}${serviceSuffix}/${actualServiceName}`
}

const partnersPortalUrl =
  (envVars.ENV === 'uat' && 'partners-uat.clipro.tv') ||
  (envVars.ENV === 'prod' && 'partners.clipro.tv')

export const BE_SERVICES = {
  LOGIN: 'login',
  BROADCAST_QUALITY: 'broadcast-quality',
  CLIPRO: 'clipro',
  SEARCH: 'search',
  PLATFORM_SEARCH: 'platform-search',
  PLATFORM: 'platform',
  ROLES: 'roles',
  OPERATIONS: 'operations',
  PUBLIC_CLIPRO_SEARCH: 'cliprosearch',
  VIDEO: 'video',
  USERS: 'users',
  ASSETS: 'assets',
  DOWNLOAD: 'download',
}

const config = {
  BACKOFFICE_GROUP_ID: backofficeGroupId,
  USER_INFO_KEY: userInfoKeyPrefix + userInfoKey,
  ...envVars,
  IS_INTERNAL_COOKIE_AUTH_SITE: isInternalCookieAuthSite,
  IS_COOKIE_AUTH: COOKIE_AUTH_SITES.includes(envVars.WEBSITE),
  PARTNERS_PORTAL_URL: partnersPortalUrl,
}

export const loadServices = () => {
  // systemHasMigrated = getSystemHasMigrated(config.IS_COOKIE_AUTH)
  config.CLIPRO_SERVICE = getServiceUrl(BE_SERVICES.CLIPRO)
  config.AUTO_TAGGING_SERVICE = getServiceUrl(envVars.USE_GATEWAY ? 'autotagapi' : 'atservice')
  config.AVGEN_SERVICE = getServiceUrl('avgenservicecore')
  config.GRAPHICS_SERVICE = getServiceUrl('graphics')
  config.ROLES_MANAGEMENT_SERVICE = getServiceUrl(BE_SERVICES.ROLES)
  config.USER_MANAGEMENT_SERVICE = getServiceUrl(BE_SERVICES.USERS)
  config.SETTINGS_SERVICE = getServiceUrl('settings')
  config.ORC_SERVICE = getServiceUrl('orc')
  config.BACKOFFICE_SERVICE = getServiceUrl('backoffice')
  config.MOVIE_CREATOR_SERVICE = getServiceUrl('mcservicecore')
  config.MAMBA_SERVICE = getServiceUrl('mamba')
  config.MAMBA_ZOOMIN_SERVICE = getServiceUrl('tagger')
  config.OFFSET_TAGGING_SERVICE = getServiceUrl(
    envVars.USE_GATEWAY ? 'offsettaggingapi' : 'offsettagging',
  )
  config.NOTIFICATIONS_SERVICE = getServiceUrl('notifications')
  config.SEARCH_SERVICE = getServiceUrl(BE_SERVICES.SEARCH)
  config.PLATFORM_SEARCH_SERVICE = getServiceUrl(BE_SERVICES.PLATFORM_SEARCH)
  config.LOGIN_SERVICE = getServiceUrl(BE_SERVICES.LOGIN)
  config.VIDEO_SERVICE = getServiceUrl(BE_SERVICES.VIDEO)
  config.OPERATIONS_SERVICE = getServiceUrl(BE_SERVICES.OPERATIONS)
  config.STORIES_SERVICE = getServiceUrl('stories')
  config.UPLOAD_SERVICE = getServiceUrl('upload')
  config.ACTION_SETTINGS_SERVICE = getServiceUrl(
    envVars.USE_GATEWAY ? 'actionsettingsapi' : 'action-settings',
  )
  config.PLATFORM_SERVICE = getServiceUrl(BE_SERVICES.PLATFORM)
  config.VIDEO_ANALYZER_SERVICE = getServiceUrl('videoanalyzerapi')
  config.VIDEO_SEARCH_SERVICE = getServiceUrl('videosearch')
  config.AVGEN_SERVICE_CORE = getServiceUrl('avgenservicecore')
  config.BI_COLLECTOR_SERVICE = getServiceUrl('bicollector')
  config.ALGO_SERVICE = getServiceUrl('generative-title')
  config.ASSETS_SERVICE = getServiceUrl(BE_SERVICES.ASSETS)
  config.BLAZE_SERVICE = getServiceUrl('blazecms')
  config.BI_DATAHUB = getServiceUrl('bidatahub')
  config.AUTOCOMPLETE = getServiceUrl('autocomplete')
  config.CLOSED_CAPTIONS_SERVICE = getServiceUrl('subtitles')
  config.GAME_RECAP_SERVICE = getServiceUrl('game-recap')
  config.ARTICLE_TO_VIDEO_SERVICE = getServiceUrl('article-to-video')
  config.PUBLIC_CLIPRO_SEARCH_SERVICE = getServiceUrl(BE_SERVICES.PUBLIC_CLIPRO_SEARCH)
  config.PUBLIC_LIBRARY_SEARCH_SERVICE = getServiceUrl('librarysearch')
  // publish and import services:
  config.PUBLISH_SERVICE = getServiceUrl('publish')
  config.IMPORT_SERVICE = getServiceUrl('import')
  config.DOWNLOAD_SERVICE = getServiceUrl(BE_SERVICES.DOWNLOAD)
  config.DISTRIBUTION_SERVICE = getServiceUrl('distribution')
}

loadServices()

console.log(`Running: ${config.WEBSITE} | Env: ${config.ENV} | Release: ${config.RELEASE}`)

export default config
